import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Navigation } from '../components/Navigation';
import { MathBackground } from '../components/MathBackground';
import { useLanguage } from '../lib/LanguageContext';
import { translations } from '../lib/translations';
import { TeamSection } from '../components/TeamSection';
function useCountUp(target: number, duration: number = 1.8, start: boolean = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime: number | null = null;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / (duration * 1000), 1);
      // ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [start, target, duration]);
  return count;
}
function AnimatedStat({
  target,
  prefix = '',
  suffix = '',
  suffixBlack = '',
  label,
  delay = 0,
  borderRight = false,
  paddingStyle = {}
}: {
  target: number;
  prefix?: string;
  suffix?: string;
  suffixBlack?: string;
  label: string;
  delay?: number;
  borderRight?: boolean;
  paddingStyle?: React.CSSProperties;
}) {
  const [inView, setInView] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setInView(true);
    }, {
      threshold: 0.3
    });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  const count = useCountUp(target, 1.8, inView);
  return <motion.div ref={ref} initial={{
    opacity: 0,
    y: 16
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    duration: 0.6,
    delay
  }} style={{
    borderRight: borderRight ? '1px solid rgba(0,0,0,0.08)' : undefined,
    ...paddingStyle
  }} data-id="element-410">
      <p style={{
      fontFamily: '"IBM Plex Sans", sans-serif',
      fontWeight: 900,
      fontSize: 'clamp(36px, 4vw, 64px)',
      lineHeight: 1,
      letterSpacing: '-0.03em',
      color: 'var(--black)',
      margin: '0 0 1rem 0'
    }} data-id="element-411">
        {prefix}
        {count}
        {suffixBlack && <span style={{
        color: 'var(--black)'
      }} data-id="element-412">
            {suffixBlack}
          </span>}
        <span style={{
        color: 'var(--red)'
      }} data-id="element-413">
          {suffix}
        </span>
      </p>
      <p style={{
      fontFamily: '"IBM Plex Sans", sans-serif',
      fontWeight: 300,
      fontSize: '0.8125rem',
      color: 'var(--grey-500)',
      lineHeight: 1.6,
      margin: 0
    }} data-id="element-414">
        {label}
      </p>
    </motion.div>;
}
export function AboutPage() {
  const {
    lang
  } = useLanguage();
  const t = translations[lang].about;
  return <div style={{
    backgroundColor: 'var(--black)',
    minHeight: '100vh'
  }} data-id="element-415">
      <Navigation data-id="element-416" />

      {/* SECTION 1 — HERO */}
      <section style={{
      position: 'relative',
      minHeight: '60vh',
      display: 'flex',
      alignItems: 'flex-end',
      overflow: 'hidden',
      paddingBottom: '6rem',
      paddingTop: '64px'
    }} data-id="element-417">
        <MathBackground data-id="element-418" />
        <div style={{
        position: 'relative',
        zIndex: 1,
        maxWidth: '64rem',
        margin: '0 auto',
        padding: '0 2rem',
        width: '100%'
      }} data-id="element-419">
          <motion.h1 initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} style={{
          fontFamily: '"IBM Plex Sans", sans-serif',
          fontWeight: 900,
          fontSize: 'clamp(40px, 7vw, 100px)',
          lineHeight: 1,
          letterSpacing: '-0.02em',
          color: 'var(--white)',
          margin: '0 0 2rem 0'
        }} data-id="element-420">
            {t.hero}
          </motion.h1>
        </div>
      </section>

      {/* WHITE CONTENT AREA */}
      <div style={{
      backgroundColor: 'var(--white)'
    }} data-id="element-421">
        {/* SECTION 2 — OUR MISSION */}
        <section style={{
        maxWidth: '64rem',
        margin: '0 auto',
        padding: '8rem 2rem',
        borderBottom: '1px solid rgba(0,0,0,0.08)'
      }} data-id="element-422">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} data-id="element-423">
            <p style={{
            fontFamily: '"IBM Plex Mono", monospace',
            fontSize: '0.65rem',
            letterSpacing: '0.3em',
            textTransform: 'uppercase',
            color: 'var(--grey-500)',
            marginBottom: '2.5rem'
          }} data-id="element-424">
              {t.missionLabel}
            </p>
            <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '4rem',
            alignItems: 'start'
          }} data-id="element-425">
              <h2 style={{
              fontFamily: '"IBM Plex Sans", sans-serif',
              fontWeight: 900,
              fontSize: 'clamp(28px, 3.5vw, 52px)',
              lineHeight: 1.05,
              letterSpacing: '-0.02em',
              color: 'var(--black)',
              margin: 0
            }} data-id="element-426">
                {t.missionTitle1}
                <span style={{
                color: 'var(--red)'
              }} data-id="element-427">
                  {t.missionTitle2}
                </span>
              </h2>
              <div data-id="element-428">
                <p style={{
                fontFamily: '"IBM Plex Sans", sans-serif',
                fontWeight: 300,
                fontSize: '1rem',
                color: 'var(--grey-700)',
                lineHeight: 1.9,
                marginBottom: '1.5rem'
              }} data-id="element-429">
                  {t.missionP1}
                </p>
                <p style={{
                fontFamily: '"IBM Plex Sans", sans-serif',
                fontWeight: 300,
                fontSize: '1rem',
                color: 'var(--grey-700)',
                lineHeight: 1.9,
                margin: 0
              }} data-id="element-430">
                  {t.missionP2}
                </p>
              </div>
            </div>
          </motion.div>
        </section>

        {/* SECTION 3 — NUMBERS */}
        <section style={{
        maxWidth: '64rem',
        margin: '0 auto',
        padding: '8rem 2rem',
        borderBottom: '1px solid rgba(0,0,0,0.08)'
      }} data-id="element-431">
          <motion.p initial={{
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} style={{
          fontFamily: '"IBM Plex Mono", monospace',
          fontSize: '0.65rem',
          letterSpacing: '0.3em',
          textTransform: 'uppercase',
          color: 'var(--grey-500)',
          marginBottom: '4rem'
        }} data-id="element-432">
            {t.numbersLabel}
          </motion.p>

          <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '0'
        }} data-id="element-433">
            <AnimatedStat target={30} suffix="+" label={t.stat1Label} delay={0} borderRight paddingStyle={{
            paddingRight: '2rem'
          }} data-id="element-434" />
            <AnimatedStat target={30} suffix="+" label={t.stat2Label} delay={0.15} borderRight paddingStyle={{
            padding: '0 2rem'
          }} data-id="element-435" />
            <AnimatedStat target={4} prefix="$" suffix="+" suffixBlack="B" label={t.stat3Label} delay={0.3} paddingStyle={{
            paddingLeft: '2rem'
          }} data-id="element-436" />
          </div>
        </section>

        {/* SECTION 4 — OUR TEAM */}
        <TeamSection data-id="element-437" />

        {/* Footer */}
        <div style={{
        maxWidth: '64rem',
        margin: '0 auto',
        padding: '4rem 2rem 6rem',
        borderTop: '1px solid rgba(0,0,0,0.08)',
        textAlign: 'center'
      }} data-id="element-438">
          <p style={{
          fontFamily: '"IBM Plex Mono", monospace',
          fontSize: '0.65rem',
          letterSpacing: '0.3em',
          textTransform: 'uppercase',
          color: 'var(--grey-500)',
          marginBottom: '0.5rem'
        }} data-id="element-439">
            White Box AI
          </p>
          <p style={{
          fontFamily: '"IBM Plex Sans", sans-serif',
          fontWeight: 300,
          fontSize: '0.875rem',
          color: 'var(--grey-500)'
        }} data-id="element-440">
            {t.footerSub}
          </p>
        </div>
      </div>
    </div>;
}